import { RevealBlock } from "./motion";
import { Box, CalendarDays, BarChart3, Wrench } from "lucide-react";

const FEATURES = [
  {
    icon: Box,
    t: "One compact unit",
    d: "A premium cashless unit positioned in an agreed terminal zone, subject to site and technical approval.",
  },
  {
    icon: CalendarDays,
    t: "Six-month learning period",
    d: "Enough time to observe seasonality, refine the range and understand genuine passenger demand.",
  },
  {
    icon: BarChart3,
    t: "Practical reporting",
    d: "Track transactions, product mix, stock availability, uptime and customer-service issues.",
  },
  {
    icon: Wrench,
    t: "Fully managed delivery",
    d: "MinisRus coordinates replenishment, monitoring, servicing and day-to-day customer support.",
  },
];

const STEPS = [
  { n: "01", t: "Align", d: "Agree on location, approved assortment, operating requirements and commercial structure." },
  { n: "02", t: "Launch", d: "Install, test and open the pilot with a clear replenishment and support plan." },
  { n: "03", t: "Learn & scale", d: "Review performance with the airport team and decide whether to refine, expand or conclude." },
];

export default function AirportPilot() {
  const smoothTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="airport-pilot" className="relative bg-ink2 py-16 md:py-24 border-t border-white/8" data-testid="airport-pilot-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-end">
          <RevealBlock className="col-span-12 lg:col-span-7">
            <p className="eyebrow mb-3">Airport Pilot Model</p>
            <h2 className="font-display font-light text-bone text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight">
              A focused trial, built to<br /><span className="text-orange">prove the opportunity.</span>
            </h2>
            <div className="mt-5 gold-rule" />
          </RevealBlock>
          <RevealBlock delay={0.08} className="col-span-12 lg:col-span-5">
            <p className="text-bone/70 text-base md:text-lg leading-relaxed max-w-xl">
              Start small, learn from real passenger behaviour and make the next decision with evidence. The final pilot structure is developed with the airport and remains subject to commercial, safety and regulatory approval.
            </p>
          </RevealBlock>
        </div>

        <div className="mt-10 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {FEATURES.map((item, i) => {
            const Icon = item.icon;
            return (
              <RevealBlock key={item.t} delay={i * 0.06} y={20} className="bg-ink border border-white/8 rounded-xl p-5 md:p-6 h-full">
                <div className="w-12 h-12 rounded-full bg-orange/15 flex items-center justify-center">
                  <Icon size={20} strokeWidth={1.7} className="text-orange" />
                </div>
                <h3 className="mt-5 text-bone text-base md:text-lg font-semibold leading-tight">{item.t}</h3>
                <p className="mt-2 text-bone/60 text-sm leading-relaxed">{item.d}</p>
              </RevealBlock>
            );
          })}
        </div>

        <div className="mt-10 md:mt-14 grid lg:grid-cols-[1fr_auto] gap-8 items-end border-t border-white/10 pt-10">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {STEPS.map((step, i) => (
              <RevealBlock key={step.n} delay={i * 0.06} y={18}>
                <p className="text-orange text-xs font-semibold tracking-wide">{step.n}</p>
                <h3 className="mt-2 text-bone text-xl font-semibold">{step.t}</h3>
                <p className="mt-2 text-bone/60 text-sm leading-relaxed">{step.d}</p>
              </RevealBlock>
            ))}
          </div>
          <RevealBlock delay={0.12}>
            <button
              onClick={() => smoothTo("#enquiries")}
              className="group inline-flex items-center gap-2 h-12 px-7 bg-orange text-ink hover:bg-orange-soft rounded-full text-[13px] font-semibold transition-colors whitespace-nowrap"
              data-testid="airport-pilot-cta"
            >
              Request a Pilot Pack
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </button>
          </RevealBlock>
        </div>
      </div>
    </section>
  );
}
